'use client';

import { Button } from '@bridebook/aura';
import { RoundedModalTitle } from '../rounded-modal/rounded-modal-header';
import { ConfirmationModalContent } from './confirmation-modal-content';
import { ComponentProps, ReactNode, useCallback, useState } from 'react';

export type ConfirmationModalAutoCloseProps = {
  /**
   * Title for the modal.
   */
  title: string;
  /**
   * Text for the modal content. Optional.
   */
  text?: string | ReactNode;
  /**
   * Text for the primary action button.
   */
  primaryButtonText: string;
  /**
   * Function to be called when the primary button is clicked.
   * If the function returns a promise, the button will be shown as loading until the promise is resolved.
   */
  onPrimaryButtonClick: () => void | Promise<void>;
  /**
   * Text for the secondary action button.
   */
  secondaryButtonText: string;
  /**
   * Function to be called when the secondary button is clicked.
   * If the function returns a promise, the button will be shown as loading until the promise is resolved.
   * It is optional, because in most cases secondary button only closes the modal. But if you need something else (like analytics) you can use this.
   */
  onSecondaryButtonClick?: () => void | Promise<void>;
  /**
   * Props for the primary button.
   */
  primaryButtonProps?: ComponentProps<typeof Button>;
  /**
   * Props for the secondary button.
   */
  secondaryButtonProps?: ComponentProps<typeof Button>;
  /**
   * Whether to show the close button in the modal header. Defaults to true.
   */
  showCloseButton?: boolean;
  /**
   * Function to be called when the modal should be closed.
   * It is used internally by the useConfirmationModal hook to close the modal after the primary/secondary actions complete.
   */
  closeModal: () => void | Promise<void>;
  /**
   * Variant for the modal. Primary, destructive, destructive-no-image.
   */
  variant?: ComponentProps<typeof ConfirmationModalContent>['variant'];
  /**
   * Variant for the modal header. "coupleside" (Playfair font) or "b2b" (regular font)
   */
  headerTitleVariant?: ComponentProps<typeof RoundedModalTitle>['variant'];
  /**
   * Optional illustration above the title. Pass a React node to show custom art; pass `null` to
   * suppress the variant default (e.g. no icon on destructive). Omitted = use variant default.
   */
  headerIllustration?: ReactNode;
};

/**
 * A wrapper component for ConfirmationModalContent that handles automatic modal closing and loading states.
 *
 * Key features:
 * - Automatically closes modal after primary/secondary actions complete
 * - Manages loading states for both buttons during async operations
 * - Handles both sync and async button click handlers
 */
export const ConfirmationModalAutoClose = ({
  title,
  text,
  primaryButtonText,
  onPrimaryButtonClick,
  secondaryButtonText,
  onSecondaryButtonClick,
  primaryButtonProps,
  secondaryButtonProps,
  showCloseButton = true,
  variant = 'primary',
  headerTitleVariant = 'coupleside',
  headerIllustration,
  closeModal,
}: ConfirmationModalAutoCloseProps) => {
  const [isLoadingPrimaryButton, setIsLoadingPrimaryButton] = useState(false);
  const [isLoadingSecondaryButton, setIsLoadingSecondaryButton] = useState(false);

  const handlePrimaryButtonClick = useCallback(async () => {
    setIsLoadingPrimaryButton(true);
    await onPrimaryButtonClick();
    setIsLoadingPrimaryButton(false);
    closeModal();
  }, [onPrimaryButtonClick, closeModal]);

  const handleSecondaryButtonClick = useCallback(async () => {
    setIsLoadingSecondaryButton(true);
    await onSecondaryButtonClick?.();
    setIsLoadingSecondaryButton(false);
    closeModal();
  }, [onSecondaryButtonClick, closeModal]);

  const disabled = isLoadingPrimaryButton || isLoadingSecondaryButton;

  return (
    <ConfirmationModalContent
      title={title}
      text={text}
      variant={variant}
      headerTitleVariant={headerTitleVariant}
      headerIllustration={headerIllustration}
      showCloseButton={showCloseButton}
      primaryButtonProps={{
        onClick: handlePrimaryButtonClick,
        loading: isLoadingPrimaryButton,
        disabled,
        children: primaryButtonText,
        ...primaryButtonProps,
      }}
      secondaryButtonProps={{
        onClick: handleSecondaryButtonClick,
        loading: isLoadingSecondaryButton,
        disabled,
        children: secondaryButtonText,
        ...secondaryButtonProps,
      }}
    />
  );
};
